// @flow
import React from "react"
import { prop } from "ramda"
import { push } from "connected-react-router"
import type { HOC, Component } from "recompose"
import { Redirect, Route } from "react-router-dom"
import withConnect from "hoc/withConnect"
import urls from "data/router/urls"
import { filter } from "data/search/actions"
import { URLParam } from "data/app/constants"
import { chooseActivity } from "data/app/actions"
import { clear } from "data/search/actions"
import { chooseGeolocation } from "data/app/actions"
import { geolocationListQuery, locationByNameQuery } from "data/app/graphql/queries"
import { parseQueryString, transformActivitiesParam } from "data/app/redirectHelpers"
import { manufacturerByNameQuery } from "data/manufacturer/queries"
import { listProductsMinimalQuery } from "data/product/graphql/queries"
import { selectLoggedIn } from "data/user/selectors"
import store from "global/store"
import { query } from "global/apolloClient/helpers"
import { noop } from "common/functions"

/**
 * Converts a standard url case insensitive dash-separated string into
 * a standard space separated name (e.g.: 'black-crows' to 'Black Crows')
 */
const convertsUrlStandardToRegularName = (rawFormat: string): string => rawFormat
    .toLowerCase()
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

/**
 * Updates the global state from the url parameters & forwards users
 * to the requested route.
 * NB: permission to a given route is defined in the backend.
 */
const LoggedOutRoute = ({
  component,
  loggedIn,
  location,
  chooseActivity,
  push,
  ...rest
}) => {
    try {
      const urlParams = location && parseQueryString(location.search)

      const includedParams = Boolean(urlParams && Object.keys(urlParams).length >= 1)

      if(includedParams) {
        clear()
      }

      //Apply currently selected activity filter to the global state.
      const productGroupParam = prop(URLParam.activity, urlParams)
      if (productGroupParam) {
        const activity = transformActivitiesParam(String(productGroupParam.toLowerCase()))
        activity && chooseActivity(activity)
      }

      //Apply currently selected brand filter to the global state.
      const brandParam = prop(URLParam.brand, urlParams)
      if (brandParam) {
        const convertedBrandName = convertsUrlStandardToRegularName(String(brandParam))

        query(manufacturerByNameQuery, { name: convertedBrandName })
          .then(brand => store.dispatch(filter({ brand: [brand] })))
          .catch(() => noop())
      }

      //Apply currently selected affiliate filter to the global state
      // & redirect users to the relevant route.
      const affiliateParam = prop(URLParam.affiliate, urlParams)
      if (affiliateParam) {
        const productParam = prop(URLParam.product, urlParams)
        if (productParam) {
          return <Redirect to={urls.product(String(productParam), String(affiliateParam))}/>
        }
        store.dispatch(filter({ affiliate: parseInt(affiliateParam, 10) }))
        return <Redirect to={urls.affiliate(String(affiliateParam))} />
      }

      //Apply currently selected location filter to the global state.
      const locationParam = prop(URLParam.location, urlParams)
      if (locationParam) {
        const convertedLocationParam = convertsUrlStandardToRegularName(String(locationParam))

        query(locationByNameQuery, { name: convertedLocationParam })
          .then(loc => store.dispatch(chooseGeolocation([loc])))
          .catch(() => noop())
      }

      // Apply product filter passed in from query parameter
      const productParam = prop(URLParam.product, urlParams)
      if (productParam) {
        const name = String(productParam)
        query(listProductsMinimalQuery, { filter: { name } })
          .then(products => store.dispatch(filter({ product: products.map(p => ({ id: p.id, name: p.name })) })))
          .catch(noop)
      }

      // If we are not searching and no filter is requested then select nearby by default
      if (location && location.pathname === urls.products &&
          !productGroupParam && !brandParam && !affiliateParam && !locationParam) {
        query(geolocationListQuery)
          .then(locations => {
            const nearby = locations.filter(location => location.nearby)
            if (nearby.length > 0) {
              store.dispatch(chooseGeolocation(nearby))
            }
          })
          .catch(noop)
      }

      // NOTE: Home page is no longer accessible - redirect to products
      if (location && location.pathname === urls.home) {
        return <Redirect to={urls.products} />
      }

    } catch (e) {
      console.warn("failed to process parameter redirect")
    }
  return <Route {...rest} component={component} />
}

const mapStateToProps = state => ({
  loggedIn: selectLoggedIn(state),
})

type Outter = {|
  component: Component<mixed>,
  path: string,
  exact?: boolean,
  location?: {
    search: string,
    pathname: string,
  },
|}
const mapDispatchToProps = {
  chooseActivity,
  clear,
  push,
}
const enhancer: HOC<*, Outter> = withConnect(
  mapStateToProps,
  mapDispatchToProps,
)

export default enhancer(LoggedOutRoute)



// WEBPACK FOOTER //
// ./src/AppComponent/Content/Routes/LoggedOutRoute.js